import { ETagVariant, ETextStyleVariant, Tag, Text } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import BeginnerFriendlyBadgeFullSvg from '@/assets/beginner-friendly/beginner-friendly-full.svg';
import SuperhostBadgeFullSvg from '@/assets/superhost/superhost-badge-full.svg';
import { EListingTileTagSlug } from '@/constants/listingTags';
import { isAutoRentalTile, TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { isCampgroundTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';

type TListingTileTagProps = {
  listingTile?: ISearchResultTile | TAutoRentalTile;
  hideTagsOnSmallScreen?: boolean;
  showNewListingTag?: boolean;
  showLimitedOfferTag?: boolean;
  showGuestFavoriteTag?: boolean;
  showSuperhostBadge?: boolean;
  showBeginnerFriendlyBadge?: boolean;
  isFeatured?: boolean;
};

export const ListingTileTag = memo(function ListingTileTag({
  listingTile,
  hideTagsOnSmallScreen,
  showNewListingTag,
  showLimitedOfferTag,
  showGuestFavoriteTag,
  showSuperhostBadge,
  showBeginnerFriendlyBadge,
  isFeatured,
}: TListingTileTagProps) {
  const intl = useIntl();

  if (listingTile && !isAutoRentalTile(listingTile) && isCampgroundTile(listingTile)) return null;

  if (showNewListingTag) {
    return (
      <div className="flex items-center justify-center h-5 m-1 bg-gray-50 -rotate-45 w-[200px] -translate-x-1/2 -translate-y-1/2">
        <Text
          component="div"
          variant={ETextStyleVariant.LegalBold}
          className="text-white uppercase">
          {intl.formatMessage({
            defaultMessage: 'New',
            id: 'bW7B87',
          })}
        </Text>
      </div>
    );
  }

  if (showLimitedOfferTag) {
    return (
      <div style={{ '--bf-tag-text-typography-text-case': 'uppercase' } as React.CSSProperties}>
        <Tag
          variant={ETagVariant.Success}
          label={intl.formatMessage({ defaultMessage: 'Limited Offer', id: 'bDCVSI' })}
          className={cn({ 'hidden sm:block': hideTagsOnSmallScreen })}
        />
      </div>
    );
  }

  // Hide superhost and guest-favorite badges when listing is featured (top pick)
  if (isFeatured) {
    return null;
  }

  const superhostTag = listingTile?.details?.tags?.find(
    rentalTag => rentalTag.slug === EListingTileTagSlug.Superhost,
  );

  if (showSuperhostBadge && superhostTag) {
    return <SuperhostBadgeFullSvg />;
  }

  const beginnerFriendlyTag = listingTile?.details?.tags?.find(
    rentalTag => rentalTag.slug === EListingTileTagSlug.BeginnerFriendly,
  );

  if (showBeginnerFriendlyBadge && beginnerFriendlyTag) {
    return <BeginnerFriendlyBadgeFullSvg />;
  }

  const guestFavoriteTag = listingTile?.details?.tags?.find(
    rentalTag => rentalTag.slug === EListingTileTagSlug.GuestFavorite,
  );

  if (showGuestFavoriteTag && guestFavoriteTag?.tag) {
    return (
      <div style={{ '--bf-tag-text-typography-text-case': 'uppercase' } as React.CSSProperties}>
        <Tag
          variant={ETagVariant.White}
          label={guestFavoriteTag.tag || ''}
          className={cn({ 'hidden sm:block': hideTagsOnSmallScreen })}
        />
      </div>
    );
  }

  return null;
});
